import React from 'react';
import {
  Table, TableHead, TableBody, TableCell, TableRow, TableContainer, useTheme,
} from '@mui/material';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { StockCheckItem, StockCheck, StockCheckDetails } from '../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import StockCountQuantityInput from './StockCountQuantityInput';
import { useReordering } from '../contexts/ReorderingContext';

interface SortableItemsTableProps {
    items: StockCheckItem[];
    onItemsReordered: (newItems: StockCheckItem[]) => void;
    categoryName: string;
    stockCheckDetail: StockCheck | undefined;
    stockCheckDetails: StockCheckDetails;
    selectedZoneId: string;
    allZonesEnabled: boolean;
    handleQuantityChange: (itemId: number | undefined, valueId: number | undefined, newQuantityString: string) => void;
    handleQuantityBlur: (itemId: number | undefined, valueId: number | undefined, newQuantityString: string) => void;
    handleItemFocus: (itemId: number) => void;
    modifiedItems: Set<number>;
    showSavedAlert: boolean;
    sectionId: string;
    onItemsOrderChanged?: (changed: boolean) => void;
}

const SortableItemsTable: React.FC<SortableItemsTableProps> = ({
  items,
  onItemsReordered,
  stockCheckDetail,
  stockCheckDetails,
  selectedZoneId,
  allZonesEnabled,
  handleQuantityChange,
  handleQuantityBlur,
  handleItemFocus,
  modifiedItems,
  showSavedAlert,
  sectionId,
  onItemsOrderChanged,
}) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const { isItemReorderingEnabled, setItemOrderChanged } = useReordering();

  const getItemId = (item: StockCheckItem, index: number) => {
    return `item-${sectionId}-${item.id || index}`;
  };

  const getUomKey = (item: StockCheckItem, uom: string) =>
    item.id ? `${item.id}-${uom}` : item.itemNumber ? `${item.itemNumber}-${uom}` : uom;

  // Handle drag end within this table component
  const handleDragEnd = (result: DropResult) => {
    // Exit early if reordering is not enabled
    if (!isItemReorderingEnabled) return;

    const { source, destination } = result;

    if (!destination || source.index === destination.index) {
      return;
    }

    const itemsCopy = Array.from(items);

    const [removed] = itemsCopy.splice(source.index, 1);
    itemsCopy.splice(destination.index, 0, removed);

    const updatedItems = itemsCopy.map((item, index) => {
      return {
        ...item,
        rowNumber: 10 + (index * 10),
      };
    });

    // Call the parent component with the updated items
    onItemsReordered(updatedItems);

    setItemOrderChanged(true);
    if (onItemsOrderChanged) {
      onItemsOrderChanged(true);
    }
  };

  // Standard non-draggable render - when toggle is off
  if (!isItemReorderingEnabled) {
    return (
      <TableContainer component="div"
        className="stockcount-table">
        <Table size="small"
          aria-label="items table">
          <TableHead>
            <TableRow>
              <TableCell align="left">{t('skuNumber')}</TableCell>
              <TableCell align="left"
                className="category-table-cell">{t('skuDescription')}</TableCell>
              <TableCell align="left">{t('quantity')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, idx) => (
              <TableRow
                key={item.id ?? item.itemNumber ?? idx}
                tabIndex={0}
                onFocus={() => handleItemFocus(item.id ?? 0)}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  borderBottom: '1px solid',
                  borderBottomColor: theme.palette.custom.gray[200],
                  backgroundColor: (item.id && modifiedItems.has(item.id) && !showSavedAlert
                    ? theme.palette.common.white
                    : theme.palette.custom.gray[50]),
                }}
              >
                <TableCell>{item.itemNumber}</TableCell>
                <TableCell className="category-table-cell">{item.description}</TableCell>
                <TableCell className="quantity-column"
                  sx={{ height: '60px', minWidth: 0 }}>
                  {stockCheckDetails?.orderedUoms && stockCheckDetails.orderedUoms.length > 0 ? (
                    <div className="quantity-fields-wrapper"
                      style={{ display: 'flex', flexWrap: 'wrap', width: '100%', gap: '8px', alignItems: 'center' }}>
                      {stockCheckDetails.orderedUoms.map((uom) => (
                        <div
                          key={getUomKey(item, uom)}
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            minWidth: '90px',
                            justifyContent: 'flex-start',
                            gap: '4px',
                          }}
                        >
                          <StockCountQuantityInput
                            item={item}
                            uom={uom}
                            stockCheckDetail={stockCheckDetail}
                            selectedZoneId={selectedZoneId}
                            allZonesEnabled={allZonesEnabled}
                            handleQuantityChange={handleQuantityChange}
                            handleQuantityBlur={handleQuantityBlur}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="quantity-fields-wrapper"
                      style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                      {['CS', 'IP', 'EA'].map(uom => (
                        <StockCountQuantityInput
                          key={getUomKey(item, uom)}
                          item={item}
                          uom={uom}
                          stockCheckDetail={stockCheckDetail}
                          selectedZoneId={selectedZoneId}
                          allZonesEnabled={allZonesEnabled}
                          handleQuantityChange={handleQuantityChange}
                          handleQuantityBlur={handleQuantityBlur}
                        />
                      ))}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Draggable view - only shown when toggle is on
  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <TableContainer component="div"
        className="stockcount-table">
        <Table size="small"
          aria-label="sortable items table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '40px', padding: '0 0 0 8px' }}>
                <DragIndicatorIcon sx={{ color: theme.palette.primary.main }} />
              </TableCell>
              <TableCell align="left">{t('skuNumber')}</TableCell>
              <TableCell align="left"
                className="category-table-cell">{t('skuDescription')}</TableCell>
              <TableCell align="left">{t('quantity')}</TableCell>
            </TableRow>
          </TableHead>

          <Droppable droppableId={`items-${sectionId}`}
            type="item">
            {(provided) => (
              <TableBody
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {items.map((item, index) => (
                  <Draggable
                    key={getItemId(item, index)}
                    draggableId={getItemId(item, index)}
                    index={index}
                  >
                    {(providedDrag, snapshot) => (
                      <TableRow
                        ref={providedDrag.innerRef}
                        {...providedDrag.draggableProps}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          borderBottom: '1px solid',
                          borderBottomColor: theme.palette.custom.gray[200],
                          backgroundColor: snapshot.isDragging
                            ? theme.palette.primary[50]
                            : (item.id && modifiedItems.has(item.id) && !showSavedAlert
                              ? theme.palette.common.white
                              : theme.palette.custom.gray[50]),
                          boxShadow: snapshot.isDragging ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                          transition: 'background-color 0.2s, box-shadow 0.2s',
                        }}
                        tabIndex={0}
                        onFocus={() => handleItemFocus(item.id ?? 0)}
                      >
                        <TableCell
                          {...providedDrag.dragHandleProps}
                          sx={{
                            width: '40px',
                            padding: '0 0 0 8px',
                            cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                          }}
                        >
                          <DragIndicatorIcon color="primary" />
                        </TableCell>
                        <TableCell>{item.itemNumber}</TableCell>
                        <TableCell className="category-table-cell">{item.description}</TableCell>
                        <TableCell className="quantity-column"
                          sx={{ height: '60px', minWidth: 0 }}>
                          {stockCheckDetails?.orderedUoms && stockCheckDetails.orderedUoms.length > 0 ? (
                            <div className="quantity-fields-wrapper"
                              style={{ display: 'flex', flexWrap: 'wrap', width: '100%', gap: '8px', alignItems: 'center' }}>
                              {stockCheckDetails.orderedUoms.map((uom) => (
                                <div
                                  key={getUomKey(item, uom)}
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    minWidth: '90px',
                                    justifyContent: 'flex-start',
                                    gap: '4px',
                                  }}
                                >
                                  <StockCountQuantityInput
                                    item={item}
                                    uom={uom}
                                    stockCheckDetail={stockCheckDetail}
                                    selectedZoneId={selectedZoneId}
                                    allZonesEnabled={allZonesEnabled}
                                    handleQuantityChange={handleQuantityChange}
                                    handleQuantityBlur={handleQuantityBlur}
                                  />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="quantity-fields-wrapper"
                              style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                              {['CS', 'IP', 'EA'].map(uom => (
                                <StockCountQuantityInput
                                  key={getUomKey(item, uom)}
                                  item={item}
                                  uom={uom}
                                  stockCheckDetail={stockCheckDetail}
                                  selectedZoneId={selectedZoneId}
                                  allZonesEnabled={allZonesEnabled}
                                  handleQuantityChange={handleQuantityChange}
                                  handleQuantityBlur={handleQuantityBlur}
                                />
                              ))}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </Table>
      </TableContainer>
    </DragDropContext>
  );
};

export default SortableItemsTable;